import React, { useEffect, useMemo, useState } from 'react';
import { CreditApplicationTemplate } from '@dill/dill-shared';
import { DillButton, DillCheckbox, DillDropdown, DillModal } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getInternalFormTemplates,
  sendInternalForm,
} from '../../../../redux/services/internalFormsService';
import { openDillToast } from '../../../../utils/helpers';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

// "Send Internal Form" on the Credit Applications page. Sending to yourself is the "I'll fill it
// in now" path — the link lands in your own inbox.
const SendInternalFormModal = ({
  open,
  handleClose,
  onSent,
}: {
  open: boolean;
  handleClose: () => void;
  onSent?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [templates, setTemplates] = useState<CreditApplicationTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [sendToMyself, setSendToMyself] = useState(true);
  const [otherEmails, setOtherEmails] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ template?: string; emails?: string }>({});
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!open || !currentSupplier?.id) return;
    setIsLoadingTemplates(true);
    dispatch(getInternalFormTemplates({ supplierId: currentSupplier.id })).then((result) => {
      setIsLoadingTemplates(false);
      if (result.type.includes('fulfilled')) {
        const fetched: CreditApplicationTemplate[] = (result as any).payload?.data ?? [];
        setTemplates(fetched);
        if (fetched.length === 1) setSelectedTemplateId(fetched[0].id);
      }
    });
  }, [open, currentSupplier?.id, dispatch]);

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name || 'Internal Form',
        value: template.id,
      })),
    [templates]
  );

  const resetAndClose = () => {
    setSelectedTemplateId('');
    setSendToMyself(true);
    setOtherEmails('');
    setComment('');
    setErrors({});
    handleClose();
  };

  const handleSend = async () => {
    const additionalEmails = parseEmails(otherEmails);
    const invalidEmail = additionalEmails.find((email) => !EMAIL_PATTERN.test(email));
    const emails = Array.from(
      new Set([
        ...(sendToMyself && user?.email ? [user.email.toLowerCase()] : []),
        ...additionalEmails,
      ])
    );
    const nextErrors = {
      template: selectedTemplateId ? '' : 'Select a form',
      emails: invalidEmail
        ? `Invalid email: ${invalidEmail}`
        : emails.length === 0
        ? 'Send to yourself or enter at least one email'
        : '',
    };
    setErrors(nextErrors);
    if (nextErrors.template || nextErrors.emails || !currentSupplier?.id) return;

    setIsSending(true);
    const result = await dispatch(
      sendInternalForm({
        supplierId: currentSupplier.id,
        templateId: selectedTemplateId,
        emails,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      })
    );
    setIsSending(false);
    if (result.type.includes('fulfilled')) {
      const failedTo: string[] = (result as any).payload?.data?.failedTo ?? [];
      openDillToast({
        type: failedTo.length > 0 ? 'ERROR' : 'SUCCESS',
        message:
          failedTo.length > 0
            ? `Form sent, but the email to ${failedTo.join(', ')} failed`
            : 'Form sent',
      });
      onSent?.();
      resetAndClose();
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result as any).payload || 'Failed to send form',
      });
    }
  };

  return (
    <DillModal
      open={open}
      onClose={resetAndClose}
      title="Send Internal Form"
      width="540px"
      maxWidth="540px"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={resetAndClose}
            isDisabled={isSending}
          />
          <DillButton
            label="Send"
            onClick={handleSend}
            isLoading={isSending}
            isDisabled={isSending || templates.length === 0}
          />
        </div>
      }>
      <div className="flex flex-col gap-4">
        {!isLoadingTemplates && templates.length === 0 ? (
          <p className="text-sm GREY_500-CLR">
            No internal forms have been set up yet. Ask your Dill admin to add one.
          </p>
        ) : (
          <DillDropdown
            label="Form"
            containerClassName="h-[40px]"
            isRequired
            isSearchable={false}
            isLoading={isLoadingTemplates}
            value={selectedTemplateId}
            items={templateOptions}
            placeholder="Select form"
            errorText={errors.template}
            onSelectChange={(item) => {
              setSelectedTemplateId(item.value);
              setErrors((prev) => ({ ...prev, template: '' }));
            }}
          />
        )}
        <div className="flex items-center gap-2">
          <DillCheckbox
            isChecked={sendToMyself}
            onChange={(checked: boolean) => {
              setSendToMyself(checked);
              setErrors((prev) => ({ ...prev, emails: '' }));
            }}
          />
          <span className="text-sm">{`Send to myself${
            user?.email ? ` (${user.email})` : ''
          }`}</span>
        </div>
        <AppInputField
          id="internalFormEmails"
          label="Other recipients"
          placeholder="Separate multiple emails with commas"
          value={otherEmails}
          errorText={errors.emails || ''}
          onTextChange={(text) => {
            setOtherEmails(text);
            setErrors((prev) => ({ ...prev, emails: '' }));
          }}
        />
        <p className="text-xs GREY_500-CLR -mt-3">
          Anyone you send it to can fill it in and sign. The first submission is the one the credit
          team reviews.
        </p>
        <AppInputField
          label="Comment"
          value={comment}
          inputType="textarea"
          onTextChange={(text) => setComment(text)}
        />
      </div>
    </DillModal>
  );
};

export default SendInternalFormModal;
